// ========================================
// PAGE BLOG
// Liste des articles
// ========================================

import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navigation from '../../components/common/Navigation';
import Footer from '../../components/common/Footer';
import LoadingScreen from '../../components/common/LoadingScreen';
import { useBlogPosts } from '../../hooks';
import { 
  FiSearch, 
  FiClock, 
  FiEye, 
  FiHeart,
  FiArrowRight,
  FiCalendar,
  FiX,
  FiBookOpen,
  FiMail
} from 'react-icons/fi';
import './Blog.css';

// ============================================
// COULEURS DES CATÉGORIES (Mapping statique)
// ============================================
const CATEGORY_COLORS = {
  communication: '#3B82F6',
  leadership: '#10B981',
  ecriture: '#8B5CF6',
  opportunites: '#F59E0B',
  technologie: '#EF4444',
  culture: '#EC4899',
  education: '#14B8A6',
  autre: '#6B7280'
};

const CATEGORY_LABELS = {
  communication: 'Communication',
  leadership: 'Leadership',
  ecriture: 'Écriture',
  opportunites: 'Opportunités',
  technologie: 'Technologie',
  culture: 'Culture',
  education: 'Éducation',
  autre: 'Autre'
};

// ============================================
// COMPOSANT PRINCIPAL
// ============================================
const Blog = () => {
  // ========== HOOK FIREBASE ==========
  // On récupère uniquement les articles publiés
  const { posts, loading } = useBlogPosts(true, 'published');

  // ========== ÉTATS LOCAUX ==========
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // ========== CATÉGORIES DYNAMIQUES ==========
  const categories = useMemo(() => {
    if (!posts?.length) return [{ id: 'all', label: 'Tous', color: '#6B7280', count: 0 }];

    // Compter les articles par catégorie
    const categoryCounts = posts.reduce((acc, post) => {
      const cat = post.category || 'autre';
      acc[cat] = (acc[cat] || 0) + 1;
      return acc;
    }, {});

    // Construire le tableau des catégories
    const cats = [
      { id: 'all', label: 'Tous', color: '#6B7280', count: posts.length }
    ];

    Object.entries(categoryCounts).forEach(([id, count]) => {
      cats.push({
        id,
        label: CATEGORY_LABELS[id] || id.charAt(0).toUpperCase() + id.slice(1),
        color: CATEGORY_COLORS[id] || '#6B7280',
        count
      });
    });

    return cats;
  }, [posts]);

  // ========== ARTICLES FILTRÉS ==========
  const filteredArticles = useMemo(() => {
    if (!posts?.length) return [];

    let result = [...posts];

    // Filtre par catégorie
    if (activeCategory !== 'all') {
      result = result.filter(article => article.category === activeCategory);
    }

    // Filtre par recherche
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(article =>
        article.title?.toLowerCase().includes(term) ||
        article.excerpt?.toLowerCase().includes(term) ||
        article.tags?.some(tag => tag.toLowerCase().includes(term))
      );
    }

    // Trier par date de publication (plus récent d'abord)
    result.sort((a, b) => {
      const dateA = a.publishedAt?.toDate ? a.publishedAt.toDate() : new Date(a.publishedAt);
      const dateB = b.publishedAt?.toDate ? b.publishedAt.toDate() : new Date(b.publishedAt);
      return dateB - dateA;
    });

    return result;
  }, [posts, activeCategory, searchTerm]);

  // ========== HELPERS ==========
  const formatDate = (dateValue) => {
    if (!dateValue) return '';
    
    // Gérer les Timestamps Firebase
    const date = dateValue?.toDate ? dateValue.toDate() : new Date(dateValue);
    
    const options = { day: 'numeric', month: 'long', year: 'numeric' };
    return date.toLocaleDateString('fr-FR', options);
  };

  const getCategoryColor = (category) => {
    return CATEGORY_COLORS[category] || '#6B7280';
  };

  const getCategoryLabel = (category) => {
    return CATEGORY_LABELS[category] || category || 'Autre';
  };

  const resetFilters = () => {
    setSearchTerm('');
    setActiveCategory('all');
  };

  // ========== ANIMATION VARIANTS ==========
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", damping: 15, stiffness: 100 }
    }
  };

  // Article mis en avant (le premier)
  const featuredArticle = filteredArticles[0];
  const otherArticles = filteredArticles.slice(1);

  // ========== RENDU LOADING ==========
  if (loading) {
    return <LoadingScreen message="Chargement des articles..." />;
  }

  // ========== RENDU PRINCIPAL ==========
  return (
    <div className="blog-page">
      <Navigation />

      {/* Hero Section */}
      <section className="blog-hero">
        <div className="hero-background">
          <div className="hero-gradient"></div>
          <div className="hero-pattern"></div>
        </div>
        
        <div className="container">
          <motion.div
            className="hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <motion.div 
              className="hero-badge"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
            >
              <FiBookOpen />
              <span>Blog</span>
            </motion.div>
            
            <h1>Réflexions & <span>Inspirations</span></h1>
            
            <p>
              Articles sur la communication, le leadership, l'écriture et les opportunités 
              pour les jeunes professionnels africains.
            </p>

            {/* Search in Hero */}
            <motion.div 
              className="hero-search"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <FiSearch className="search-icon" />
              <input
                type="text"
                placeholder="Rechercher un article..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button 
                  className="clear-search"
                  onClick={() => setSearchTerm('')}
                  aria-label="Effacer la recherche"
                >
                  <FiX />
                </button>
              )}
            </motion.div>
          </motion.div>
        </div>

        {/* Decorative Elements */}
        <div className="hero-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
        </div>
      </section>

      {/* Categories */}
      <section className="categories-section">
        <div className="container">
          <motion.div
            className="categories-wrapper"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            {categories.map(category => (
              <motion.button
                key={category.id}
                className={`category-pill ${activeCategory === category.id ? 'active' : ''}`}
                onClick={() => setActiveCategory(category.id)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                style={{
                  '--category-color': category.color
                }}
              >
                <span className="category-dot"></span>
                {category.label}
                <span className="category-count">{category.count}</span>
              </motion.button>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Articles Section */}
      <section className="articles-section">
        <div className="container">
          {filteredArticles.length === 0 ? (
            <motion.div
              className="no-results"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <div className="no-results-icon">
                <FiBookOpen />
              </div>
              <h3>Aucun article trouvé</h3>
              <p>Essayez de modifier vos critères de recherche</p>
              <button 
                className="btn btn-primary"
                onClick={resetFilters}
              >
                Réinitialiser les filtres
              </button>
            </motion.div>
          ) : (
            <>
              {/* Featured Article */}
              {featuredArticle && activeCategory === 'all' && !searchTerm && (
                <motion.article
                  className="featured-article"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <Link to={`/blog/${featuredArticle.slug}`}>
                    <div className="featured-image">
                      {featuredArticle.coverImage ? (
                        <img src={featuredArticle.coverImage} alt={featuredArticle.title} />
                      ) : (
                        <div className="featured-placeholder">
                          <span>{featuredArticle.title?.charAt(0) || 'A'}</span>
                        </div>
                      )}
                      <div className="featured-overlay">
                        <span 
                          className="featured-category"
                          style={{ backgroundColor: getCategoryColor(featuredArticle.category) }}
                        >
                          {getCategoryLabel(featuredArticle.category)}
                        </span>
                      </div>
                    </div>
                    
                    <div className="featured-content">
                      <div className="featured-meta">
                        <span className="meta-item">
                          <FiCalendar />
                          {formatDate(featuredArticle.publishedAt)}
                        </span>
                        <span className="meta-item">
                          <FiClock />
                          {featuredArticle.readTime || 5} min de lecture
                        </span>
                      </div>

                      <h2>{featuredArticle.title}</h2>
                      <p>{featuredArticle.excerpt}</p>

                      <div className="featured-footer">
                        <div className="article-stats">
                          <span><FiEye /> {featuredArticle.views || 0}</span>
                          <span><FiHeart /> {featuredArticle.likes || 0}</span>
                        </div>
                        <span className="read-more">
                          Lire l'article
                          <FiArrowRight />
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.article>
              )}

              {/* Articles Grid */}
              <motion.div
                className="articles-grid"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                key={`${activeCategory}-${searchTerm}`}
              >
                <AnimatePresence mode="popLayout">
                  {(activeCategory === 'all' && !searchTerm ? otherArticles : filteredArticles).map((article) => (
                    <motion.article
                      key={article.id}
                      className="article-card"
                      variants={itemVariants}
                      layout
                      whileHover={{ y: -8 }}
                    >
                      <Link to={`/blog/${article.slug}`}>
                        <div className="article-image">
                          {article.coverImage ? (
                            <img src={article.coverImage} alt={article.title} />
                          ) : (
                            <div className="article-placeholder">
                              <span>{article.title?.charAt(0) || 'A'}</span>
                            </div>
                          )}
                          <span 
                            className="article-category"
                            style={{ backgroundColor: getCategoryColor(article.category) }}
                          >
                            {getCategoryLabel(article.category)}
                          </span>
                        </div>

                        <div className="article-content">
                          <div className="article-meta">
                            <span>
                              <FiCalendar />
                              {formatDate(article.publishedAt)}
                            </span>
                            <span>
                              <FiClock />
                              {article.readTime || 5} min
                            </span>
                          </div>

                          <h3>{article.title}</h3>
                          <p>{article.excerpt}</p>

                          <div className="article-footer">
                            <div className="article-stats">
                              <span><FiEye /> {article.views || 0}</span>
                              <span><FiHeart /> {article.likes || 0}</span>
                            </div>
                            <FiArrowRight className="arrow-icon" />
                          </div>
                        </div>
                      </Link>
                    </motion.article>
                  ))}
                </AnimatePresence>
              </motion.div>

              {/* Results count */}
              <motion.div
                className="results-info"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                {filteredArticles.length} article{filteredArticles.length > 1 ? 's' : ''} trouvé{filteredArticles.length > 1 ? 's' : ''}
              </motion.div>
            </>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="newsletter-section">
        <div className="container">
          <motion.div
            className="newsletter-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="newsletter-content">
              <div className="newsletter-icon">
                <FiMail />
              </div>
              <h2>Restez informé</h2>
              <p>
                Recevez mes derniers articles et opportunités directement dans votre boîte mail.
              </p>
              <div className="newsletter-form">
                <input type="email" placeholder="Votre adresse email" />
                <button className="btn btn-primary">
                  S'abonner
                </button>
              </div>
              <span className="newsletter-note">
                Pas de spam. Désabonnement possible à tout moment.
              </span>
            </div>
            
            <div className="newsletter-decoration">
              <div className="decoration-circle"></div>
              <div className="decoration-circle"></div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Blog;